
import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useSyncExternalStore } from 'react';
import { Report, UserProfile, VorankuendigungData, VorankuendigungArchiveEntry } from '../../types';
import { Button } from '../Button';
import {
    FileDown, Copy, FileText, Edit2, Archive, FileClock, Save, Upload, Info, RotateCcw,
    FolderOpen, FolderInput, Loader2, CheckCircle2, AlertTriangle, X, Printer,
} from 'lucide-react';
import { FormEditor } from '../FormEditor';
import FormPreview from '../FormPreview';
import { AuthorityManager } from './AuthorityManager';
import { generateVorankuendigungPDF } from '../../services/pdfService';
import { BillingSelectionModal } from '../BillingSelectionModal';
import {
    appendVorankuendigungVersion,
    createVorankuendigungVersion,
    describeVorankuendigungVersion,
    restoreVorankuendigungVersion,
} from '../../services/documentVersioning';
import { isHourlyUnit } from '../../services/projectLv';
import { selectBillableServices } from '../../services/billingSelection';
import { useBestandteilAbschluss } from '../bestandteile/useBestandteilAbschluss';
import { BestandteilAbschlussHinweis } from '../bestandteile/BestandteilAbschlussHinweis';
import { BestandteilStundenPflicht } from '../bestandteile/BestandteilStundenPflicht';
import { bestimmeAbschlussFall, schonGebuchtHinweis, stundenFensterOffen } from '../bestandteile/abschlussLogic';
import { vorankAutospeicherStand } from './vorankAutospeicher';
import { exportBuchungStand, projektDesVorgangs } from '../bestandteile/abschlussSchreibweg';
import { JournalMirrorRequest, queueJournalMirror } from '../../services/serviceJournal';
import {
    isManualField,
    normalizeVorankuendigung,
} from '../../services/vorankuendigungModel';
import { gleichePunktFuenfAb } from '../../services/vorankuendigungKoordinatoren';
import {
    ABGLEICH_FELD_LABEL,
    ABGLEICH_FELDER,
    AbgleichFeld,
    gleichePunktEinsZweiAb,
} from '../../services/vorankuendigungAbgleich';
import { useRechte } from '../../hooks/useRechte';
import { DokumentePanel } from '../dokumente/DokumentePanel';
import { readOnlyTitle } from '../../services/readOnlyNotice';
import { useProjektSteckbrief } from '../../services/projektSteckbrief';
import {
    vorankuendigungVorschlaege,
    wendeUebernahmeAn,
    type VorankVorschlag,
} from '../../services/vorankuendigungSteckbrief';
import {
    fetchPlanungsfragen,
    fetchPlanungsphase,
    markiereUebernommen,
} from '../../services/planungsphaseService';
import { describeError } from '../../services/errorMessage';
import { VorankuendigungPlanungsLeiste, type StempelMeldung } from './VorankuendigungPlanungsLeiste';
import { AbweichungsHinweis } from '../common/AbweichungsHinweis';
import {
    VorankAblageAuftrag,
    VorankFassung,
    ablageAbsage,
    ablageHinweise,
    ablageWarnung,
    bereiteVorankAblageVor,
    entwurfsFassung,
    fassungAusArchiv,
    legeVorankAuftragAb,
    vorankAblageTitel,
    vorankAuftragFuerWiederholung,
} from '../../services/vorankuendigungAblage';
import { PlotterDialog } from '../sige-plan/PlotterDialog';
import {
    legePlotAbrechnungZurueck,
    meldePlotAngekommen,
    merkePlotAuftrag,
    nimmPlotAbrechnung,
    offenePlotAbrechnungen,
    plotAbrechnungenAbonnieren,
    type OffenePlotAbrechnung,
    type VorankPlotStand,
} from '../sige-plan/plotAbrechnungen';
import type { PlotDokument } from '../../services/plotter/plotTypen';
import { erzeugeVorankPlotBlatt, type VorankPlotBlatt } from '../../services/pdf/vorankPlotBlatt';
import { useSigePlanVorlagen } from '../../services/sigePlanVorlagen';
import {
    PLOT_NICHT_ABGERECHNET_TEXT,
    VORANK_FASSUNG_FRAGE_TITEL,
    VORANK_NICHT_ABLEGEN_HINWEIS,
    VORANK_PLOT_KNOPF_TITEL,
    belastbareProjektId,
    plotAbrechnungAbholbar,
    vorankAblageFehlerZusatz,
    vorankAbrechnungFensterOffen,
    vorankAbrechnungsSchritt,
    vorankNachPositionsauswahl,
    vorankAbrechnungsTitel,
    vorankAbschlussSatz,
    vorankAbschlussTitel,
    vorankArchivBezeichnung,
    vorankFassungFrageSatz,
    vorankPlotAbrechnungFehlerText,
    vorankPlotBauFehlerText,
    vorankPlotDokument,
    vorankPlotSperre,
    vorankProtokollText,
    vorankStartfrageNoetig,
    type VorankAbrechnungsZiel,
    type VorankAusgabeArt,
} from './vorankPlot';
import { ladeFassungen, vorankHash } from '../../services/vorank/vorankUnterschriftService';
import { mitZeitlimit } from '../../services/ablageHilfen';
import type { VorankFassungZeile } from '../../services/vorank/vorankUnterschriftTypes';
import { VorankUnterschriftKarte } from './VorankUnterschriftKarte';
import {
    bereiteVorankExportVor,
    unterschriebenePdfBytes,
    type VorankExportVorbereitet,
} from './vorankUnterschriftAblauf';
import {
    VORANK_EXPORT_PROJEKT_GEWECHSELT,
    vorankBegleitblattHinweis,
    vorankExportPdfFehlerText,
    vorankBegleitblattPlan,
    vorankPlotFrage,
    vorankPlotQuelle,
    vorankPlotStandFuer,
} from './vorankUnterschriftAnsicht';

interface Props {
    report: Report;
    onUpdate: (report: Report) => void;
    onBack: () => void;
    saveStatus?: 'idle' | 'saving' | 'saved' | 'error';
    // NEW: Sidebar control
    activeSection?: string;
    onSectionChange?: (section: string) => void;
    /** Lesemodus des Projekts. Die Formularseite hat keinen eigenen Lesemodus
     *  — ihre Eingaben laufen ueber onUpdate und verpuffen dort still, dafuer
     *  steht der Balken des Aufrufers. Der Behoerden-Reiter ist die Ausnahme:
     *  er schreibt in die GLOBALE Behoerdentabelle und wird deshalb hier
     *  durchgereicht und dort gesperrt. */
    isReadOnly?: boolean;
    /** Nur fuer die Geld-Wache im Abrechnungsdialog (Rolle). Sonst unbenutzt. */
    userProfile?: UserProfile | null;
}

const DEFAULT_VORANK: VorankuendigungData = {
    sender: '',
    recipient: '',
    siteAddress: '',
    clientName: '',
    clientAddress: '',
    clientPhone: '',
    clientEmail: '',
    clientPersons: [],
    projectDescription: '',
    responsibleThirdParty: '',
    responsibleThirdParties: [],

    coordinatorPlanningCompany: '',
    coordinatorPlanningPerson: '',
    planningCoordinatorId: undefined,
    useOwnCompanyForPlanning: false, // Default to false (User must opt-in)
    planningCoordinators: [],

    coordinatorExecutionCompany: '',
    coordinatorExecutionPerson: '',
    executionCoordinatorId: undefined,
    executionCoordinators: [],

    startDate: '',
    endDate: '',
    maxWorkers: '',
    numEmployers: '',
    numContractors: '',
    employersList: Array(12).fill(''),
    manualFields: [],
    hasDangerousWork: false,
    selectedDangerousWorks: [],
    dangerousWorkDescription: '',
    hasSiteManager: false,
    siteManager: '',
    locationDate: '',
    signerName: '',
    history: [],
    archive: []
};

/**
 * Was die letzte Ablage in „Dokumente" ergeben hat (Stufe 1A, E17). Beim
 * Fehler bleibt der fertige Auftrag (Blob + Kennung) stehen, damit „Erneut
 * ablegen" DASSELBE Blatt noch einmal schickt — nicht den Stand, der
 * inzwischen im Editor ist. `auftrag` ist nur dann null, wenn schon das PDF
 * nicht entstand; dann setzt der erneute Versuch es aus dem Schnappschuss neu.
 */
type AblageStand =
    | { art: 'laeuft'; fassung: VorankFassung }
    /**
     * `warnung`: die Fassung liegt, aber der Kern hat etwas zu melden (verwaiste Datei) — gelb, kein Retry.
     * `hinweise`: Sätze des Kerns, die keine Fehler sind (etwa zum Journal-Eintrag, Stufe 6.3) — dezent darunter.
     */
    | { art: 'abgelegt' | 'schon_abgelegt'; fassung: VorankFassung; titel: string; warnung: string | null; hinweise: string[] }
    | {
        art: 'fehler';
        fassung: VorankFassung;
        fehler: string;
        auftrag: VorankAblageAuftrag | null;
        snapshot: VorankuendigungData;
        /** Woraus die Fassung kam — der Satz „… selbst ist gültig“ nennt beim Plot keinen Download. */
        ausgabe: VorankAusgabeArt;
    }
    | { art: 'uebersprungen'; fassung: VorankFassung; grund: string };

/**
 * Ein offener Plotter-Dialog. Projekt und Stand sind beim KLICK festgehalten:
 * abgerechnet wird später genau dieser Stand in genau diesem Projekt — auch
 * wenn bis zum Ankommen des Auftrags weitergeschrieben wurde.
 */
interface VorankPlotter {
    blob: Blob;
    blatt: { breiteMm: number; hoeheMm: number; gekappt: boolean };
    dokument: PlotDokument;
    projektId: string;
    stand: VorankPlotStand;
}

/** Eine Zeile Ergebnis der Ablage — unter der Werkzeugleiste und im Abschlussdialog. */
const AblageHinweis: React.FC<{
    stand: AblageStand;
    isReadOnly: boolean;
    onErneut: () => void;
    onSchliessen?: () => void;
}> = ({ stand, isReadOnly, onErneut, onSchliessen }) => {
    const was = stand.fassung.anlass === 'entwurf' ? 'Der Entwurf' : 'Die Fassung';
    let farbe = 'bg-slate-50 border-slate-200 text-slate-700';
    let icon = <Loader2 className="w-4 h-4 shrink-0 mt-0.5 animate-spin" />;
    let text = `${was} wird in Dokumente abgelegt…`;
    if (stand.art === 'abgelegt' || stand.art === 'schon_abgelegt') {
        text = stand.art === 'abgelegt'
            ? `In Dokumente abgelegt: „${stand.titel}".`
            : `Dieser Stand liegt bereits in Dokumenten: „${stand.titel}" — nichts doppelt angelegt.`;
        if (stand.warnung) {
            // Gelungen, aber nicht sauber: der Warnsatz des Kerns gehört dazu,
            // sonst sähe eine liegengebliebene Datei wie voller Erfolg aus.
            farbe = 'bg-amber-50 border-amber-200 text-amber-800';
            icon = <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />;
            text = `${text} ${stand.warnung}`;
        } else {
            farbe = 'bg-emerald-50 border-emerald-200 text-emerald-800';
            icon = <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />;
        }
    } else if (stand.art === 'fehler') {
        farbe = 'bg-rose-50 border-rose-200 text-rose-800';
        icon = <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />;
        text = `${was} konnte nicht in Dokumente abgelegt werden: ${stand.fehler}`
            + vorankAblageFehlerZusatz(stand.fassung.anlass, stand.ausgabe);
    } else if (stand.art === 'uebersprungen') {
        farbe = 'bg-amber-50 border-amber-200 text-amber-800';
        icon = <Info className="w-4 h-4 shrink-0 mt-0.5" />;
        text = stand.grund;
    }
    // Hinweise des Kerns zu einer gelungenen Ablage — keine Fehler, deshalb
    // dezent unter dem Satz und ohne Knopf.
    const hinweise = (stand.art === 'abgelegt' || stand.art === 'schon_abgelegt') ? stand.hinweise : [];
    return (
        <div className={`text-xs border rounded-lg px-3 py-2 flex items-start gap-2 print:hidden ${farbe}`}>
            {icon}
            <span className="flex-1 min-w-0 break-words">
                {text}
                {hinweise.length > 0 && (
                    <ul className="mt-1 space-y-0.5 text-[11px] font-normal text-slate-600">
                        {hinweise.map((h, i) => <li key={i} className="flex items-start gap-1"><Info className="w-3 h-3 shrink-0 mt-0.5 text-slate-400" /><span>{h}</span></li>)}
                    </ul>
                )}
            </span>
            {stand.art === 'fehler' && (
                <button
                    type="button"
                    onClick={onErneut}
                    disabled={isReadOnly}
                    title={isReadOnly ? readOnlyTitle('Die Ablage lässt sich nicht wiederholen.') : 'Dieselbe Fassung noch einmal in Dokumente ablegen'}
                    className="shrink-0 flex items-center gap-1 text-[11px] font-bold text-rose-700 bg-white border border-rose-200 hover:bg-rose-100 px-2 py-1 rounded disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                    <RotateCcw className="w-3 h-3" /> Erneut ablegen
                </button>
            )}
            {onSchliessen && stand.art !== 'laeuft' && (
                <button
                    type="button"
                    onClick={onSchliessen}
                    title="Hinweis ausblenden"
                    className="shrink-0 p-0.5 rounded hover:bg-black/5 transition"
                >
                    <X className="w-3.5 h-3.5" />
                </button>
            )}
        </div>
    );
};

/**
 * Nach einer Übernahme aus der Planungsphase die zugehörigen Fragen als
 * übernommen stempeln (`planungsfragen.uebernommen_am`, Entscheid E7).
 *
 * Fail-open: der Wert steht zu diesem Zeitpunkt schon im Feld (setData),
 * der Stempel ist ein Vermerk dazu. Scheitert er, meldet die Leiste das in
 * einer Zeile — nie still, aber auch nie als Riegel. Der Steckbrief kennt
 * die Fragen nur mit Code, nicht mit Id; die Ids werden deshalb hier gelesen,
 * erst beim Klick, nie beim Rendern.
 */
const stempleUebernommeneFragen = async (projectId: string, codes: readonly string[]): Promise<StempelMeldung | null> => {
    if (!codes.length) return null;
    const liste = codes.join(', ');
    try {
        const phase = await fetchPlanungsphase(projectId);
        if (!phase) {
            return { art: 'fehler', text: `Der Wert ist übernommen; der Vermerk an Frage ${liste} wurde nicht gesetzt: keine Planungsphase gefunden.` };
        }
        const fragen = await fetchPlanungsfragen(phase.id);
        const ids = fragen.filter(f => codes.includes(f.code)).map(f => f.id);
        if (!ids.length) {
            return { art: 'fehler', text: `Der Wert ist übernommen; Frage ${liste} wurde in der Planungsphase nicht gefunden, kein Vermerk gesetzt.` };
        }
        const n = await markiereUebernommen(ids, new Date().toISOString());
        return {
            art: 'ok',
            text: n > 0
                ? `Frage ${liste} ist in der Planungsphase als übernommen vermerkt.`
                : `Frage ${liste} war in der Planungsphase schon als übernommen vermerkt.`,
        };
    } catch (e) {
        return { art: 'fehler', text: `Der Wert ist übernommen; der Vermerk an Frage ${liste} ist nicht gelungen: ${describeError(e)}` };
    }
};

export const VorankuendigungView: React.FC<Props> = ({
    report, onUpdate, onBack,
    activeSection = 'editor',
    isReadOnly = false,
    userProfile = null
}) => {
    // Fallback if not controlled by sidebar
    const currentTab = activeSection;

    // Startfrage „Vorankündigung öffnen“: schon gebucht → schon erstellt.
    // Als ANFANGSWERT, nicht per Mount-Effekt (vorankStartfrageNoetig): so
    // steht sie schon im ersten Render fest, und der Plot-Effekt unten holt
    // keinen angekommenen Plot ab, solange sie offen ist. Der frühere Effekt
    // lief ebenfalls nur einmal beim Mount — mit demselben `report`, denn die
    // App mountet die Ansicht nur mit Bericht.
    const [showInitDialog, setShowInitDialog] = useState(
        () => vorankStartfrageNoetig(report.metadata.globalData?.services),
    );
    const [showBillingModal, setShowBillingModal] = useState(false);
    const [showVersionDialog, setShowVersionDialog] = useState(false);
    const [versionLabel, setVersionLabel] = useState('');
    // Preise im Abrechnungsdialog nur mit dem Recht „Preise sehen" (Admins immer).
    const rechte = useRechte(userProfile?.role);
    /**
     * Abschlussschritt nach dem VERSAND (Plan Paketposition B5): die
     * Vorankuendigung ist der einzige Bestandteil, der erst mit dem Versand
     * zaehlt (E3). Der Export legt die Fassung als versendet ab; die Ausgabe,
     * die dabei entsteht, erledigt den Bestandteil per Trigger, und der Hook
     * bucht ein dadurch vollstaendiges Paket (Fall 2/3).
     */
    const abschluss = useBestandteilAbschluss({ report, onUpdate, userProfile, quelle: 'vorankuendigung', isReadOnly });
    const [dokumenteOffen, setDokumenteOffen] = useState(false);
    const [ablageStand, setAblageStand] = useState<AblageStand | null>(null);
    /**
     * Nach dem Export offen, bis der Anwender zurück will — zeigt das Ablage-Ergebnis.
     * `art`: derselbe Dialog nach der Abrechnung eines Plots, dann ohne „heruntergeladen“.
     */
    const [exportAbschluss, setExportAbschluss] = useState<{
        gebucht: boolean;
        hinweis?: string | null;
        art: VorankAusgabeArt;
        /** Was aus dem Begleitblatt wurde (mit/ohne Unterschrift-Link) — nur beim Export. */
        begleitblatt?: { ton: 'info' | 'warnung'; text: string } | null;
    } | null>(null);
    /**
     * Haken „Begleitblatt mit Unterschrift-Link anhängen“ (Plan Vorankündigung
     * unterschreiben §5), Vorgabe an. Nicht gespeichert — jeder Export fragt neu.
     */
    const [begleitblattAn, setBegleitblattAn] = useState(true);
    /**
     * Der Export bereitet PDF, Fassung und Begleitblatt vor (bis zu 8 s für
     * die Fassung). Solange: Knopf mit Spinner, kein zweiter Lauf, kein
     * Plot-Abholen. Der Ref ist der Riegel, der Zustand nur die Anzeige.
     */
    const [exportBereitet, setExportBereitet] = useState(false);
    const exportLaeuftRef = useRef(false);
    /** Riegel, solange der Plot-Klick die Fassungen frisch liest (Doppelklick). */
    const plotQuelleLaeuftRef = useRef(false);
    /** Rückfrage beim Plot: unterschrieben ist nur ein älterer Stand (Plan §9). */
    const [plotQuellenFrage, setPlotQuellenFrage] = useState<{ fassung: VorankFassungZeile; projektId: string } | null>(null);
    /**
     * Wofür die Positionsauswahl offen ist: Export oder ein schon gesendeter
     * Plot (vorankPlot.ts, wie `abrechnungsZiel` im SiGe-Plan-Editor).
     * Gesetzt, bevor ein Abrechnungsschritt beginnt.
     */
    const [abrechnungsZiel, setAbrechnungsZiel] = useState<VorankAbrechnungsZiel>({ art: 'export' });
    /**
     * Offene Frage „Als Fassung ausgeben?“ nach einem Plot im Paket-Fall
     * (vorankAbrechnungsSchritt) — mit dem Plot, für den sie gilt. Solange sie
     * offen ist, bleibt er in `plotInAbrechnungRef`: wird die Ansicht dabei
     * verlassen, legt der Unmount-Effekt ihn zurück, und die Frage kommt beim
     * nächsten Öffnen wieder.
     */
    const [plotFassungFrage, setPlotFassungFrage] = useState<Extract<VorankAbrechnungsZiel, { art: 'plot' }> | null>(null);
    /** Firmen-Standardbreite der Plotterrolle — der Aushang nimmt immer sie. */
    const plotVorlagen = useSigePlanVorlagen();
    /** Das Aushang-Blatt entsteht (A4-PDF → Seiten nebeneinander). */
    const [plotBaut, setPlotBaut] = useState(false);
    /** Offener Plotter-Dialog — mit dem Stand, der beim Klick eingefroren wurde. */
    const [plotter, setPlotter] = useState<VorankPlotter | null>(null);
    /** Eine Zeile zum Plot: Fehler beim Bauen/Abrechnen oder „nicht abgerechnet“. */
    const [plotMeldung, setPlotMeldung] = useState<{ ton: 'fehler' | 'hinweis'; text: string } | null>(null);
    // Angekommene Plot-Aufträge aller Projekte, die auf ihre Abrechnung warten
    // (plotAbrechnungen.ts) — abgeholt werden nur die Vorankündigungen dieses Projekts.
    const offenePlots = useSyncExternalStore(plotAbrechnungenAbonnieren, offenePlotAbrechnungen, offenePlotAbrechnungen);
    /**
     * Die abgeholte Plot-Abrechnung, solange sie nicht abgerechnet ist. Beim
     * Abholen verlässt sie die Warteschlange; wird die Ansicht mit offener
     * Positionsauswahl verlassen oder das Projekt gewechselt, geht sie dorthin
     * zurück (Codex 23.10.2026) — sonst wäre der Plot nie gebucht.
     */
    const plotInAbrechnungRef = useRef<OffenePlotAbrechnung<'vorank'> | null>(null);
    useEffect(() => () => {
        const eintrag = plotInAbrechnungRef.current;
        plotInAbrechnungRef.current = null;
        if (eintrag) legePlotAbrechnungZurueck(eintrag);
    }, []);

    // Aus der Planungsphase (Stufe 3.1): der Projektsteckbrief wird nur
    // GELESEN. Die Leiste rechnet beim Rendern; geschrieben wird erst per
    // Klick über setData — bewusst NICHT im Abgleich-Effekt unten (Falle 3
    // des Plans: der Effekt speichert über den Autospeicher in die DB).
    const steckbriefProjektId = (report.metadata.globalData?.projectId || '').trim();
    const steckbriefStand = useProjektSteckbrief(steckbriefProjektId, report.metadata);
    /** In dieser Sitzung ausgeblendete Zeilen der Leiste — nichts davon wird gespeichert. */
    const [planungAusgeblendet, setPlanungAusgeblendet] = useState<ReadonlySet<string>>(() => new Set());
    const [stempelMeldung, setStempelMeldung] = useState<StempelMeldung | null>(null);
    /**
     * Nach „Neue Fassung anlegen" im Abweichungshinweis (Stufe 6.1): die
     * Vorankündigung hat keinen eigenen Entwurfsweg — die neue Fassung ist
     * der nächste Export. Der Satz sagt das; gesetzt wird nichts.
     */
    const [neueFassungHinweis, setNeueFassungHinweis] = useState(false);

    /**
     * Fassungen zum Unterschreiben (`vorank_fassungen`, Plan §4). Gelesen beim
     * Öffnen, bei Projektwechsel und nach eigenen Aktionen (`ladeFassungenNeu`)
     * — kein Dauer-Polling. `fuer` trennt die Projekte; `liste: null` heißt
     * „nicht verfügbar“ (SQL fehlt, Netz): Karte still, Plot und Export wie
     * bisher (fail-open). Gesetzt wird nur in der Antwort, nie synchron im Effekt.
     */
    const fassungsProjektId = belastbareProjektId(report.metadata.globalData?.projectId);
    const [fassungsStand, setFassungsStand] = useState<{ fuer: string; liste: VorankFassungZeile[] | null } | null>(null);
    const [fassungenLauf, setFassungenLauf] = useState(0);
    useEffect(() => {
        if (!fassungsProjektId) return;
        let gilt = true;
        void ladeFassungen(fassungsProjektId).then(
            liste => { if (gilt) setFassungsStand({ fuer: fassungsProjektId, liste }); },
            () => { if (gilt) setFassungsStand({ fuer: fassungsProjektId, liste: null }); },
        );
        return () => { gilt = false; };
    }, [fassungsProjektId, fassungenLauf]);
    const fassungen = fassungsStand && fassungsStand.fuer === fassungsProjektId ? fassungsStand.liste : null;
    const ladeFassungenNeu = useCallback(() => setFassungenLauf(n => n + 1), []);

    // Initialize state with report data or defaults
    // Altstände werden EINMAL beim Laden in das Listenformat gehoben. Danach
    // sind die Listen samt ihrer IDs stabil — sonst würde jeder Render neue IDs
    // erzeugen und der Editor verlöre bei jedem Tastendruck den Fokus.
    const [data, setData] = useState<VorankuendigungData>(() => {
        if (report.vorankuendigung) {
            return normalizeVorankuendigung({ ...DEFAULT_VORANK, ...report.vorankuendigung });
        }
        return { ...DEFAULT_VORANK };
    });

    // EFFECT: Initialize or Auto-Update Data from Metadata
    useEffect(() => {
        const meta = report.metadata;
        const settings = report.settings;
        const currentData = { ...data }; // Work on a copy of current state
        
        let hasChanges = false;
        
        // --- AUTO SYNC LOGIC ---
        
        // 1. + 2. Ort der Baustelle und Bauherr (Name, Anschrift)
        //
        // Nur LEERE Felder werden gefüllt. Weicht ein gefüllter Wert von den
        // Projektdaten ab, bleibt er stehen; der Editor zeigt dazu einen
        // Hinweis mit Übernahme-Knopf (siehe `abweichungen` beim Rendern).
        // Vorher ersetzte der Effekt beide Punkte bei jeder Abweichung still,
        // also auch Handkorrekturen des Anwenders. Die Komma-Korrektur der
        // Projektadresse („Straße, Nr. PLZ" → „Straße Nr., PLZ") steckt in
        // services/vorankuendigungAbgleich.ts und ist dort geprüft.
        const punktEinsZwei = gleichePunktEinsZweiAb(currentData, meta);
        ABGLEICH_FELDER.forEach(feld => {
            const wert = punktEinsZwei.fuellen[feld];
            if (wert === undefined) return;
            currentData[feld] = wert;
            hasChanges = true;
        });

        // 2. Bauherr — Telefon und E-Mail (ebenfalls nur, wenn leer)
        if (meta.clientPhone && !currentData.clientPhone) {
            currentData.clientPhone = meta.clientPhone;
            hasChanges = true;
        }
        if (meta.clientEmail && !currentData.clientEmail) {
            currentData.clientEmail = meta.clientEmail;
            hasChanges = true;
        }

        // 3. Sender Pre-fill (Default to Client if empty)
        if (!currentData.sender && meta.clientName) {
            const formatAddr = (addr?: string) => addr ? addr.replace(/,\s*/g, '\n') : '';
            currentData.sender = [
                meta.clientName,
                formatAddr(meta.clientAddress),
                meta.clientContactPerson || null, 
                meta.clientPhone ? `Tel: ${meta.clientPhone}` : null,
                meta.clientEmail ? `E-Mail: ${meta.clientEmail}` : null
            ].filter(Boolean).join('\n');
            hasChanges = true;
        }

        // 4. Project Dates (Format ISO to DD.MM.YYYY)
        const formatDate = (d?: string) => d ? new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '';
        
        // Updated Logic: Only set if empty to avoid overwriting manual edits
        if (meta.globalData?.startDate && !currentData.startDate) {
            const formattedStart = formatDate(meta.globalData.startDate);
            if (currentData.startDate !== formattedStart) {
                currentData.startDate = formattedStart;
                hasChanges = true;
            }
        }
        if (meta.globalData?.endDate && !currentData.endDate) {
            const formattedEnd = formatDate(meta.globalData.endDate);
            if (currentData.endDate !== formattedEnd) {
                currentData.endDate = formattedEnd;
                hasChanges = true;
            }
        }

        // 5. Koordinatoren (Punkt 5 a/b)
        //
        // Die Liste darf mehrere Personen führen (Vertreter, zweiter SiGeKo).
        // Der Abgleich befüllt sie deshalb nur, solange sie leer ist, und zieht
        // danach ausschliesslich die Firmenanschrift der Einträge nach, die
        // nachweislich aus den Stammdaten stammen (`coordinatorId`). Alles von
        // Hand Ergänzte bleibt unangetastet — sonst löschte der nächste
        // Metadaten-Wechsel die zusätzlichen Personen wieder weg.
        //
        // Wer nach 5a und wer nach 5b gehört, entscheidet die Rolle
        // „Verantwortliche Planungsphase" (Entscheid 12.10.): gibt es sie,
        // stehen diese Personen unter 5a und der Hauptverantwortliche mit den
        // Ausführungs-Vertretern unter 5b. Ohne sie bleibt es beim bisherigen
        // Weg (5a nur mit „Eigene Firma"). Die Regeln stecken in
        // services/vorankuendigungKoordinatoren.ts und sind dort geprüft.
        const companyString = [
            settings?.companyName,
            settings?.companyAddress
        ].filter(Boolean).join('\n');

        const abgleich = gleichePunktFuenfAb(currentData, meta, companyString);
        if (abgleich.planningCoordinators) {
            currentData.planningCoordinators = abgleich.planningCoordinators;
            hasChanges = true;
        }
        if (abgleich.executionCoordinators) {
            currentData.executionCoordinators = abgleich.executionCoordinators;
            hasChanges = true;
        }

        // 6. Firmenliste und Anzahl (Punkt 8 & 10)
        //
        // Von Hand gesetzte Werte sind gesperrt: der Anwender hat die Punkte
        // 7–10 bewusst überschrieben, und die Vorankündigung schreibt nichts
        // davon in die Projektdaten zurück.
        if (meta.distributionList && meta.distributionList.length > 0) {
            const companiesSet = new Set<string>();

            meta.distributionList.filter(c => c.inAdvanceNotice !== false).forEach(c => {
                if (c.company && c.company.trim().length > 1) {
                    companiesSet.add(c.company.trim());
                }
            });

            const uniqueCompanies = Array.from(companiesSet).sort();

            // Punkt 8: Anzahl
            const countStr = uniqueCompanies.length.toString();
            if (!isManualField(currentData, 'numEmployers') && currentData.numEmployers !== countStr) {
                currentData.numEmployers = countStr;
                hasChanges = true;
            }

            // Punkt 10: Liste (mindestens 12 Zeilen, damit das Blatt Formular bleibt)
            if (!isManualField(currentData, 'employersList')) {
                const rows = Math.max(12, uniqueCompanies.length);
                const newEmployersList = Array(rows).fill('');
                uniqueCompanies.forEach((comp, idx) => {
                    newEmployersList[idx] = comp;
                });

                if (JSON.stringify(currentData.employersList) !== JSON.stringify(newEmployersList)) {
                    currentData.employersList = newEmployersList;
                    hasChanges = true;
                }
            }
        }

        // 8. Location/Date: Do NOT overwrite if empty, leave free text as requested

        if (hasChanges) {
            setData({ ...currentData });
        }
        
    }, [report.metadata, report.settings, data.useOwnCompanyForPlanning]); // Added dependency to re-trigger if checkbox changes

    // Der Bericht von JETZT, nicht der aus dem Render, in dem der Timer
    // armiert wurde (Muster UnterlageView `reportRef`): zwischen Armieren und
    // Feuern kann der Abschlussschritt eine Paketposition gebucht haben
    // (`BST-…`, Plan Bestandteile B5) — der alte Bericht aus der Closure
    // setzte diese Buchung sonst zurueck. Siehe vorankAutospeicherStand.
    // Im Layout-Effekt: synchron im Commit, bevor ein Timer dazwischenkommt.
    const reportRef = useRef(report);
    useLayoutEffect(() => { reportRef.current = report; }, [report]);

    // Sync local state to report parent state on change
    useEffect(() => {
        const timer = setTimeout(() => {
            // Bewusst erst HIER gelesen — beim Feuern, nicht beim Armieren.
            const neu = vorankAutospeicherStand(reportRef.current, data);
            if (neu) onUpdate(neu);
        }, 2000); // Verzögerung auf 2 Sekunden erhöht
        return () => clearTimeout(timer);
    }, [data]);

    const handleRegeneratePdf = (entry: VorankuendigungArchiveEntry) => {
        try {
            const doc = generateVorankuendigungPDF(entry.snapshot);
            // entry.date ist ein ISO-Zeitstempel — Doppelpunkte sind in Dateinamen unzulässig.
            const safeName = describeVorankuendigungVersion(entry).replace(/[\\/:*?"<>|]/g, '_');
            const filename = `Vorankündigung_${safeName}.pdf`;
            doc.save(filename);
        } catch (e) {
            console.error("Regeneration Error", e);
            alert("Fehler beim Generieren der archivierten PDF.");
        }
    };

    // Version speichern: hängt den aktuellen Stand als benannten Archiveintrag an.
    // Sofortiges onUpdate, damit die Version auch dann liegt, wenn der Nutzer
    // das Modul vor dem nächsten Autospeichern verlässt.
    const handleSaveVersion = () => {
        const entry = createVorankuendigungVersion(data, versionLabel, new Date().toISOString(), crypto.randomUUID());
        const updatedData: VorankuendigungData = {
            ...data,
            archive: appendVorankuendigungVersion(data.archive, entry),
        };
        setData(updatedData);
        onUpdate({ ...report, vorankuendigung: updatedData });
        setVersionLabel('');
        setShowVersionDialog(false);
    };

    // Version laden: ersetzt den Inhalt des laufenden Dokuments, Archiv/Verlauf bleiben.
    const handleLoadVersion = (entry: VorankuendigungArchiveEntry) => {
        const name = describeVorankuendigungVersion(entry);
        if (!window.confirm(`Version "${name}" in den Editor laden?\n\nDer aktuelle Inhalt der Vorankündigung wird dabei ersetzt. Das Archiv bleibt vollständig erhalten.`)) {
            return;
        }
        const restored = restoreVorankuendigungVersion(data, entry);
        setData(restored);
        onUpdate({ ...report, vorankuendigung: restored });
    };

    // Ablage in „Dokumente" (Stufe 1A, E17). Kein Effekt, kein setData: die
    // Ablage ist ein Zusatz, kein Riegel — scheitert sie, bleiben Download,
    // Buchung und Archiv-Schnappschuss gültig, und der Hinweis bietet „Erneut
    // ablegen" für DIESELBE Fassung (gleiche fassungRef, die Ablage erkennt
    // einen schon gelandeten Versuch als `schon_abgelegt`).
    const fuehreAblageAus = async (
        auftrag: VorankAblageAuftrag, fassung: VorankFassung, snapshot: VorankuendigungData,
        ausgabe: VorankAusgabeArt = 'export',
    ) => {
        setAblageStand({ art: 'laeuft', fassung });
        const ergebnis = await legeVorankAuftragAb(auftrag);
        if (ergebnis.status === 'fehler') {
            // Festgehalten wird der Auftrag MIT dem Steckbrief-Anker dieses
            // Versuchs: „Erneut ablegen" friert dann den Stand der Ausgabe
            // ein, nicht den inzwischen geänderten.
            setAblageStand({
                art: 'fehler', fassung, fehler: ergebnis.fehler || 'Unbekannter Fehler.',
                auftrag: vorankAuftragFuerWiederholung(auftrag, ergebnis), snapshot, ausgabe,
            });
            return;
        }
        setAblageStand({
            art: ergebnis.status,
            fassung,
            titel: ergebnis.dokument?.titel || vorankAblageTitel(fassung),
            warnung: ablageWarnung(ergebnis),
            hinweise: ablageHinweise(ergebnis),
        });
        // Der VERSAND ist die Ausgabe, die den Bestandteil erledigt (E3) —
        // nachgefragt wird mit genau ihrer Kennung. Ein Entwurf ist keine
        // Ausgabe. Ohne Paket dieser Quelle tut der Hook nichts.
        if (fassung.anlass === 'versand') void abschluss.nachAblage(ergebnis);
    };

    /**
     * `wiederholung`: der erste Versuch kam nicht bis zum Auftrag (PDF
     * scheiterte) — dann wird KEIN frischer Steckbrief-Anker gebildet. Der
     * Stand, mit dem die Fassung hinausging, liegt nicht vor, und ein
     * heutiger verdeckte spätere Änderungen; der Kern sagt dann, dass die
     * Fassung nicht überwacht wird.
     *
     * `ausgabe`: Export oder Plot — nur für den Satz bei einem Fehlschlag.
     */
    const starteAblage = (
        snapshot: VorankuendigungData, fassung: VorankFassung, wiederholung = false,
        ausgabe: VorankAusgabeArt = 'export',
    ) => {
        const projektId = (report.metadata.globalData?.projectId || '').trim();
        const absage = ablageAbsage({ projectId: projektId, isReadOnly });
        if (absage) {
            setAblageStand({ art: 'uebersprungen', fassung, grund: absage });
            return;
        }
        // Blatt und Auftrag entstehen EINMAL; der Auftrag bleibt für „Erneut
        // ablegen" festgehalten — es geht immer dasselbe Blatt hinaus. Die
        // Projektdaten gehen mit: beim Versand legt der Kern den Journal-
        // Eintrag mit Steckbrief-Auszug an (Stufe 6.1), beim Entwurf nicht.
        const vorbereitung = bereiteVorankAblageVor({ projectId: projektId, snapshot, fassung, metadata: report.metadata });
        if (vorbereitung.status === 'fehler') {
            setAblageStand({ art: 'fehler', fassung, fehler: vorbereitung.fehler, auftrag: null, snapshot, ausgabe });
            return;
        }
        const auftrag = wiederholung ? vorankAuftragFuerWiederholung(vorbereitung.auftrag, null) : vorbereitung.auftrag;
        void fuehreAblageAus(auftrag, fassung, snapshot, ausgabe);
    };

    // Nur per Knopf (E17): Entwürfe landen nie von selbst in der Ablage. Kein
    // Archiv-Schnappschuss, keine Buchung — nur das Blatt, als Entwurf
    // beschriftet. Die Kennung kommt aus dem Inhalt: derselbe Stand zweimal
    // geklickt ergibt „schon abgelegt", keine zweite Zeile.
    const handleEntwurfAblegen = () => {
        starteAblage(data, entwurfsFassung(new Date().toISOString(), data));
    };

    // Erneuter Versuch mit dem FESTGEHALTENEN Auftrag (gleiches Blatt, gleiche
    // Kennung): ging die Antwort des ersten Versuchs nur verloren, findet die
    // Ablage die Fassung über die Kennung wieder (`schon_abgelegt`).
    const handleErneutAblegen = () => {
        if (ablageStand?.art !== 'fehler') return;
        if (ablageStand.auftrag) {
            void fuehreAblageAus(ablageStand.auftrag, ablageStand.fassung, ablageStand.snapshot, ablageStand.ausgabe);
            return;
        }
        // Ohne festgehaltenen Auftrag (PDF scheiterte): neu setzen, aber ohne
        // frischen Anker — siehe `starteAblage`.
        starteAblage(ablageStand.snapshot, ablageStand.fassung, true, ablageStand.ausgabe);
    };

    /**
     * Der Abschlussschritt beginnt mit dem Klick (Plan B5):
     *  - Fall 1/3 (eigene Vorankuendigungs-Position): die Positionsauswahl
     *    wie bisher;
     *  - Fall 2 (nur ein Paket) und „weder noch": KEIN Auswahldialog — der
     *    Export laeuft ohne Buchung, die Bestandteile meldet der
     *    Abschlussdialog;
     *  - Nur-Ansicht/Freelancer: keine Auswahl (gebucht wuerde nichts).
     *
     * Derselbe Weg nach dem Plotten (offene Plot-Abrechnungen, unten), nur
     * ohne Download: das Blatt ist schon gedruckt. Einzige Abweichung: in
     * Fall 2 fragt der Plot erst „Als Fassung ausgeben?“ (wie der SiGe-Plan),
     * denn erst die Ablage als Fassung erledigt den Bestandteil im Paket.
     * Die Regel steckt in `vorankAbrechnungsSchritt`. Das Ziel steht VOR dem
     * Dialog fest — die Bestätigung liest es (`executeExport`).
     */
    const starteAbrechnung = (ziel: VorankAbrechnungsZiel) => {
        setAbrechnungsZiel(ziel);
        const fall = bestimmeAbschlussFall({ positionen: billingServices, pakete: abschluss.pakete });
        const schritt = vorankAbrechnungsSchritt({ ziel: ziel.art, fall, sperre: abschluss.sperre });
        if (schritt === 'positionsauswahl') {
            setShowBillingModal(true);
            return;
        }
        if (schritt === 'fassung_fragen' && ziel.art === 'plot') {
            setPlotFassungFrage(ziel);
            return;
        }
        fuehreAusgabeAus(ziel, null);
    };

    const handleExportClick = () => {
        // Ein Export bereitet noch vor (Fassung, Begleitblatt): kein zweiter.
        if (exportLaeuftRef.current) return;
        starteAbrechnung({ art: 'export' });
    };

    /**
     * Bestätigung der Positionsauswahl — für das Ziel, mit dem sie aufging.
     * Plot in Fall 3 mit „Vorgang ohne Leistungsbuchung“: erst die Frage
     * „Als Fassung ausgeben?“ (`vorankNachPositionsauswahl`). Die Abrechnung
     * bleibt solange in `plotInAbrechnungRef` — die Antwort erledigt sie.
     */
    const executeExport = (serviceIdToBill: string | null, hours: number = 1) => {
        if (abrechnungsZiel.art === 'plot' && vorankNachPositionsauswahl({
            ziel: abrechnungsZiel.art,
            fall: bestimmeAbschlussFall({ positionen: billingServices, pakete: abschluss.pakete }),
            gebuchtePosition: serviceIdToBill,
            sperre: abschluss.sperre,
        }) === 'fassung_fragen') {
            setShowBillingModal(false);
            setPlotFassungFrage(abrechnungsZiel);
            return;
        }
        fuehreAusgabeAus(abrechnungsZiel, serviceIdToBill, hours);
    };

    /**
     * Der gemeinsame Kern von Export und Plot: Buchung, Leistungsprotokoll,
     * Journal-Vormerkung, Archiv, Ablage, Abschlussdialog. Nur der Export
     * erzeugt und lädt das PDF herunter. Beim Plot gehen Archiv-Schnappschuss
     * und Ablage aus dem GEPLOTTETEN Stand hervor, der Editorinhalt `data`
     * bleibt dabei stehen — der Anwender kann seit dem Plot weitergeschrieben
     * haben; angehängt werden nur Verlauf und Archiv.
     *
     * Beim Export kommt das PDF fertig aus `fuehreAusgabeAus` (`vorbereitet`:
     * Vorankündigung + Begleitblatt, Archiv-Id, Stand beim Klick); dasselbe
     * gilt dann für den Export wie früher für den Plot: archiviert und abgelegt
     * wird der Stand, der auf dem Blatt steht, `data` bleibt Editorinhalt.
     */
    const schliesseAusgabeAb = (
        ziel: VorankAbrechnungsZiel, serviceIdToBill: string | null, hours: number = 1,
        vorbereitet: VorankExportVorbereitet | null = null,
    ) => {
        const exportStand = ziel.art === 'export' && vorbereitet?.art === 'ok' ? vorbereitet : null;
        if (ziel.art === 'export' && !exportStand) {
            // Das PDF ließ sich nicht erzeugen: nichts heruntergeladen, nichts
            // gebucht. Geprüft VOR dem try — ein `throw` darin versteht der
            // React-Compiler nicht und gäbe die ganze Komponente auf.
            const fehler = vorbereitet?.art === 'fehler' ? vorbereitet.fehler : null;
            console.error('PDF Export Error', fehler);
            setShowBillingModal(false);
            setPlotMeldung({ ton: 'fehler', text: vorankExportPdfFehlerText(fehler ? describeError(fehler) : '') });
            return;
        }
        if (ziel.art === 'plot') {
            const eintrag = plotInAbrechnungRef.current;
            plotInAbrechnungRef.current = null;
            // Projekt seit dem Abholen gewechselt: nicht ins falsche Projekt
            // buchen — zurück in die Warteschlange, das eigene holt sie wieder.
            if (eintrag && belastbareProjektId(reportRef.current.metadata.globalData?.projectId) !== eintrag.projektId) {
                legePlotAbrechnungZurueck(eintrag);
                setShowBillingModal(false);
                return;
            }
        }
        // Die Zweitschrift ins Buchungsjournal wird nur VORGEMERKT (diese
        // Ansicht speichert nicht selbst, sie reicht ueber onUpdate an die
        // Offline-Queue weiter) und erst HINTER dem try gestellt: dessen catch
        // zeigt dem Anwender einen Export-Fehler, und ein Stolpern der
        // Zweitschrift darf nie so aussehen.
        let vormerkung: JournalMirrorRequest | null = null;
        // Die Ablage in „Dokumente" startet aus demselben Grund erst hinter dem
        // try — und wartet nicht auf sich selbst: der Export ist mit dem Download
        // fertig (der Plot mit dem gesendeten Auftrag), die Ablage meldet ihr
        // Ergebnis im Abschlussdialog nach.
        let ablage: { snapshot: VorankuendigungData; fassung: VorankFassung } | null = null;
        try {
            // Nur der Export lädt herunter — der Plot ist schon gedruckt. Das
            // PDF (samt Begleitblatt) ist vorbereitet (oben geprüft).
            if (ziel.art === 'export' && exportStand) {
                const filename = `Vorankündigung_neu.pdf`;
                exportStand.doc.save(filename);
            }

            // --- ACCOUNTING & HISTORY LOGIC ---
            // Der Stand, der JETZT gilt — ueber den gemeinsamen Ref-Schreibweg
            // des Moduls (Codex R2c): ein Abschlussschritt einer frueheren
            // Ablage kann inzwischen ein Paket gebucht haben (BST-…), und der
            // Stand aus dem Render setzte diese Buchung sonst zurueck.
            const stand = abschluss.holeBericht() ?? report;
            const projektDesExports = projektDesVorgangs(stand);
            const today = new Date().toISOString();
            const services = stand.metadata.globalData?.services || [];
            let updatedServices = [...services];
            let serviceLog = stand.metadata.globalData?.serviceLog || [];
            
            // Default action type
            let actionType: 'created' | 'revised' = 'created';
            if (data.history && data.history.length > 0) actionType = 'revised';

            // Bill Selected Service
            // „gebucht" erst, wenn der Schreibweg angenommen hat (Codex R3).
            let gebucht = false;
            const sIndex = serviceIdToBill ? updatedServices.findIndex(s => s.id === serviceIdToBill) : -1;
            if (serviceIdToBill) {
                if (sIndex !== -1) {
                    const s = updatedServices[sIndex];
                    const increment = isHourlyUnit(s.unit) ? hours : 1;
                    const newUsed = (s.quantityUsed || 0) + increment;
                    const isDone = s.quantityContracted > 0 && newUsed >= s.quantityContracted;
                    
                    updatedServices[sIndex] = {
                        ...s,
                        quantityUsed: newUsed,
                        status: isDone ? 'done' : s.status
                    };

                    const previousLog = serviceLog;
                    const logEntry = {
                         id: crypto.randomUUID(),
                         date: today,
                         serviceId: s.id,
                         quantity: increment,
                         performerId: 'system',
                         performerName: stand.metadata.coordinator || 'System',
                         isSubstitution: false,
                         description: vorankProtokollText(ziel.art, actionType, increment, s.unit)
                    };
                    serviceLog = [...previousLog, logEntry];

                    // Diese Ansicht bekommt kein Projekt-Objekt, nur den
                    // Bericht: die echte Zeile in `projects` steht in
                    // `globalData.projectId`. 'temp-id' ist das Kennzeichen
                    // eines noch nie gespeicherten Projekts — darunter wird
                    // NICHTS vorgemerkt.
                    const projektId = (stand.metadata.globalData?.projectId || '').trim();
                    if (projektId && projektId !== 'temp-id') {
                        vormerkung = {
                            projectId: projektId,
                            entries: [logEntry],
                            previousLog,
                            source: 'einweisung',
                            linkedProjectConfig: stand.metadata.globalData?.linkedProjectConfig ?? null,
                            // Sicher gewusst: der Export (wie der Plot) der
                            // Vorankuendigung ist weder Zusatzleistung noch
                            // Nachweis einer BEGEHUNG.
                            statements: { [logEntry.id]: { isAddon: false, coversInspection: false } },
                        };
                    } else {
                        console.warn('[VorankuendigungView] Buchung ohne belastbare Projekt-id — nicht im Journal vorgemerkt.');
                    }
                }
            }

            // Archiviert wird, was hinausging: beim Export der Stand auf dem
            // Blatt (beim Klick), beim Plot der Stand beim Klick auf „An
            // Plotter senden“ bzw. der Inhalt der geplotteten unterschriebenen
            // Fassung — nicht der Editorstand beim Ankommen des Auftrags.
            // Die Archiv-Id des Exports steht schon an der Fassung (Plan §5).
            const ausgegeben = ziel.art === 'plot' ? ziel.stand.snapshot : (exportStand?.snapshot ?? data);
            const archiveEntry = createVorankuendigungVersion(
                ausgegeben, vorankArchivBezeichnung(ziel.art, actionType), today, exportStand?.archivId ?? crypto.randomUUID(),
            );
            // Abgelegt wird der Schnappschuss, nicht `data`: beim Export sind
            // beide jetzt gleich, aber nur der Schnappschuss bleibt es (beim
            // Plot ist er ohnehin der geplottete Stand) — und seine Id ist die
            // Fassungskennung in der Ablage.
            ablage = { snapshot: archiveEntry.snapshot, fassung: fassungAusArchiv(archiveEntry) };

            const newHistory = [...(data.history || []), { date: today, action: actionType }];
            const newArchive = appendVorankuendigungVersion(data.archive, archiveEntry);

            // Der Inhalt bleibt `data` — auch beim Plot nie der Schnappschuss,
            // sonst verschwände, was seit dem Plot geschrieben wurde.
            const updatedData = { ...data, history: newHistory, archive: newArchive };
            setData(updatedData);

            const verworfen = abschluss.schreibeFuer(projektDesExports, {
                ...stand,
                vorankuendigung: updatedData,
                metadata: {
                    ...stand.metadata,
                    globalData: {
                        ...stand.metadata.globalData,
                        services: updatedServices,
                        serviceLog: serviceLog
                    }
                }
            });
            const buchung = exportBuchungStand({ gewaehlt: serviceIdToBill, positionGefunden: sIndex !== -1, verworfen });
            gebucht = buchung.gebucht;
            // Nicht geschrieben (Projekt gewechselt, Position verschwunden) →
            // auch nichts vormerken und nichts als gebucht melden. Der Inhalt
            // bleibt im Editor und geht mit dem Autospeicher.
            if (!gebucht) vormerkung = null;

            setShowBillingModal(false);
            // Früher ging es hier sofort zurück zum Projekt. Jetzt bleibt die
            // Ansicht für den Abschlussdialog stehen, damit das Ergebnis der
            // Ablage sichtbar wird; „Zurück zum Projekt" ruft onBack.
            setExportAbschluss({
                gebucht,
                hinweis: buchung.hinweis,
                art: ziel.art,
                begleitblatt: exportStand ? vorankBegleitblattHinweis(exportStand.begleitblatt) : null,
            });

        } catch (e) {
            if (ziel.art === 'plot') {
                // Inline statt Browser-Dialog: die Abrechnung kann ohne
                // Klick des Anwenders beginnen (Auftrag angekommen).
                console.error("Plot Abrechnung Error", e);
                setPlotMeldung({ ton: 'fehler', text: vorankPlotAbrechnungFehlerText(describeError(e)) });
            } else {
                console.error("PDF Export Error", e);
                alert("Fehler beim Exportieren der PDF.");
            }
            setShowBillingModal(false);
        }
        if (vormerkung) queueJournalMirror(vormerkung);
        if (ablage) starteAblage(ablage.snapshot, ablage.fassung, false, ziel.art);
    };

    // Nach der Vorbereitung des Exports schließt der Kern des JÜNGSTEN Renders
    // ab (Leistungen, Pakete, Editorinhalt) — nicht der aus dem Klick.
    const schliesseAusgabeAbRef = useRef(schliesseAusgabeAb);
    useEffect(() => { schliesseAusgabeAbRef.current = schliesseAusgabeAb; });

    /**
     * Ausgabe starten. Der Plot geht direkt in den Kern (sein Blatt ist schon
     * gedruckt). Der Export bereitet erst vor (Plan Vorankündigung
     * unterschreiben §5): PDF erzeugen, Seitenzahl merken, Fassung anlegen
     * (8 s, fail-open), Begleitblatt anhängen — dann Download, Buchung,
     * Protokoll, Journal, Archiv und Ablage in derselben Reihenfolge wie
     * bisher. Asynchron ohne `try…finally` (React-Compiler, siehe
     * `baueVorankPlot`); die Vorbereitung wirft nie.
     */
    const fuehreAusgabeAus = (ziel: VorankAbrechnungsZiel, serviceIdToBill: string | null, hours: number = 1) => {
        if (ziel.art !== 'export') {
            schliesseAusgabeAb(ziel, serviceIdToBill, hours);
            return;
        }
        if (exportLaeuftRef.current) return;
        exportLaeuftRef.current = true;
        setExportBereitet(true);
        setShowBillingModal(false);
        const projektBeimKlick = (report.metadata.globalData?.projectId || '').trim();
        void bereiteVorankExportVor({
            snapshot: data,
            plan: vorankBegleitblattPlan({ haken: begleitblattAn, isReadOnly, projektId: fassungsProjektId }),
            fassungen,
            kontaktMail: userProfile?.email || null,
            origin: window.location.origin,
            pathname: window.location.pathname,
        }).then(vorbereitet => {
            exportLaeuftRef.current = false;
            setExportBereitet(false);
            if (vorbereitet.art === 'ok' && vorbereitet.fassung) ladeFassungenNeu();
            // Projekt während der Vorbereitung gewechselt: der Kern schriebe
            // Archiv und Ablage des alten Stands ins neue Projekt — abbrechen.
            if ((reportRef.current.metadata.globalData?.projectId || '').trim() !== projektBeimKlick) {
                setPlotMeldung({ ton: 'hinweis', text: VORANK_EXPORT_PROJEKT_GEWECHSELT });
                return;
            }
            schliesseAusgabeAbRef.current(ziel, serviceIdToBill, hours, vorbereitet);
        });
    };

    /**
     * „An Plotter senden“ (Aushang). Die Quelle wählt `vorankPlotQuelle`
     * (Plan §9): unterschriebene Fassung mit genau diesem Stand → sie wird
     * geplottet; unterschrieben ist nur ein älterer Stand → Rückfrage; keine
     * Unterschrift → der Stand wie bisher, ohne Rückfrage (E1).
     *
     * Die Fassungen werden VOR der Wahl frisch gelesen: die Ansicht lädt sie
     * nur beim Öffnen und nach eigenen Aktionen — unterschreibt der Bauherr,
     * während die Ansicht offen ist, ginge sonst der unsignierte Stand ohne
     * Rückfrage an den Plotter. Scheitert das Lesen, gilt der geladene Stand.
     */
    const handlePlotClick = async () => {
        const projektId = belastbareProjektId(report.metadata.globalData?.projectId);
        if (isReadOnly || plotBaut || plotQuelleLaeuftRef.current || !projektId) return;
        plotQuelleLaeuftRef.current = true;
        // Höchstens 5 s: hängt das Netz, gilt der geladene Stand — der Knopf
        // soll nicht still tot bleiben.
        const frisch = await mitZeitlimit(ladeFassungen(projektId), 5_000).catch(() => null);
        plotQuelleLaeuftRef.current = false;
        // Inzwischen ein anderes Projekt offen: der Klick galt dem alten.
        if (belastbareProjektId(reportRef.current.metadata.globalData?.projectId) !== projektId) return;
        if (frisch) setFassungsStand({ fuer: projektId, liste: frisch });
        const liste = frisch ?? fassungen;
        const quelle = vorankPlotQuelle(liste, liste ? vorankHash(data) : null);
        if (quelle.art === 'frage') {
            setPlotQuellenFrage({ fassung: quelle.fassung, projektId });
            return;
        }
        void baueVorankPlot(projektId, quelle.art === 'unterschrieben' ? quelle.fassung : null);
    };

    /** Antwort auf die Rückfrage — `true`: die unterschriebene (ältere) Fassung plotten. */
    const beantwortePlotQuellenFrage = (unterschrieben: boolean) => {
        const frage = plotQuellenFrage;
        setPlotQuellenFrage(null);
        if (!frage) return;
        // Projekt inzwischen gewechselt: die Frage galt dem alten.
        if (belastbareProjektId(reportRef.current.metadata.globalData?.projectId) !== frage.projektId) return;
        void baueVorankPlot(frage.projektId, unterschrieben ? frage.fassung : null);
    };

    /**
     * Das Blatt bauen und den Plotter-Dialog öffnen. Der Stand JETZT wird
     * eingefroren — gebucht, archiviert und abgelegt wird später GENAU er,
     * auch wenn bis zum Ankommen des Auftrags weitergeschrieben wird; bei der
     * unterschriebenen Fassung ist es ihr Inhalt. Das Blatt entsteht aus dem
     * A4-PDF — beim Stand wie beim Export, bei der unterschriebenen Fassung
     * aus Inhalt + Unterschrift (digital) bzw. der eingelesenen PDF (Papier),
     * nie mit Begleitblatt —, immer auf der Standardbreite der Firma.
     * Abgerechnet wird erst, wenn der Auftrag beim Server angelegt ist
     * (plotAbrechnungen.ts), nicht beim Öffnen des Dialogs.
     */
    const baueVorankPlot = async (projektId: string, unterschriebeneFassung: VorankFassungZeile | null) => {
        const stand: VorankPlotStand = vorankPlotStandFuer(data, unterschriebeneFassung);
        const dokument = vorankPlotDokument({
            projectId: projektId,
            projektNummer: report.metadata.projectNumber,
            projektName: report.metadata.projectName,
        });
        setPlotBaut(true);
        setPlotMeldung(null);
        // Bewusst ohne `finally`: an einem try…finally gibt der React-Compiler
        // die ganze Komponente auf — und mit ihr verstummen seine Lint-Regeln
        // für diese Datei (die CI-Ratsche zählte dann zu wenig).
        let blatt: VorankPlotBlatt;
        try {
            const a4 = unterschriebeneFassung
                ? await unterschriebenePdfBytes(unterschriebeneFassung)
                : generateVorankuendigungPDF(stand.snapshot).output('arraybuffer');
            blatt = await erzeugeVorankPlotBlatt(a4, plotVorlagen.standardBreite);
        } catch (e) {
            console.error('Vorankündigung Plot-Blatt Error', e);
            setPlotBaut(false);
            setPlotMeldung({ ton: 'fehler', text: vorankPlotBauFehlerText(describeError(e)) });
            return;
        }
        setPlotBaut(false);
        // Inzwischen ein anderes Projekt offen: kein Dialog — der Auftrag
        // gehörte dem alten, abgerechnet würde er dort nie gesehen.
        if (belastbareProjektId(reportRef.current.metadata.globalData?.projectId) !== projektId) return;
        setPlotter({
            blob: blatt.blob,
            blatt: { breiteMm: blatt.breiteMm, hoeheMm: blatt.hoeheMm, gekappt: false },
            dokument,
            projektId,
            stand,
        });
    };

    const schliessePlotter = useCallback(() => setPlotter(null), []);

    /**
     * Positionsauswahl geschlossen, ohne zu bestätigen. Beim Export heißt das
     * Abbrechen. Beim Plot ist das Blatt schon gedruckt — trotzdem wird dann
     * nichts gebucht, archiviert oder abgelegt (wie „Nicht ablegen“ im
     * SiGe-Plan): die Ablage als Versand erledigte per Trigger den
     * Bestandteil und könnte ein Paket buchen, und genau das hat der Anwender
     * eben abgelehnt. Für „ablegen, aber nicht buchen“ gibt es im Dialog
     * „Vorgang ohne Leistungsbuchung abschließen“. Gesagt wird es trotzdem.
     */
    const schliesseAbrechnung = () => {
        setShowBillingModal(false);
        if (abrechnungsZiel.art !== 'plot') return;
        lassePlotUnabgerechnet();
    };

    /**
     * Der abgeholte Plot bleibt bewusst unabgerechnet — Positionsauswahl
     * geschlossen oder „Nicht ablegen“ auf die Frage „Als Fassung ausgeben?“.
     * Er verlässt die Abrechnung (kein Zurücklegen beim Unmount), und die
     * gelbe Zeile sagt es.
     */
    const lassePlotUnabgerechnet = () => {
        const eintrag = plotInAbrechnungRef.current;
        plotInAbrechnungRef.current = null;
        // Projekt inzwischen gewechselt: das Schließen (bzw. „Nicht ablegen“)
        // galt nicht diesem Plot.
        if (eintrag && belastbareProjektId(reportRef.current.metadata.globalData?.projectId) !== eintrag.projektId) {
            legePlotAbrechnungZurueck(eintrag);
            return;
        }
        setPlotMeldung({ ton: 'hinweis', text: PLOT_NICHT_ABGERECHNET_TEXT });
    };

    /**
     * Frage „Als Fassung ausgeben?“ (Paket-Fall, nur beim Plot — Fall 2 sofort,
     * Fall 3 nach „Vorgang ohne Leistungsbuchung“). „Als Fassung
     * ausgeben“ geht den bisherigen Direktweg: archivieren, als versendet
     * ablegen — das erledigt den Bestandteil und bucht ggf. das Paket.
     * „Nicht ablegen“ wie das Schließen der Positionsauswahl beim Plot.
     */
    const beantwortePlotFassungFrage = (alsFassung: boolean) => {
        const ziel = plotFassungFrage;
        setPlotFassungFrage(null);
        if (!ziel) return;
        if (alsFassung) fuehreAusgabeAus(ziel, null);
        else lassePlotUnabgerechnet();
    };

    const handleDialogChoice = (choice: 'edit' | 'new') => {
        if (choice === 'new') {
            // Logic to clear form if needed
        }
        setShowInitDialog(false);
    };
    
    // Buchbare LV-Positionen: Zuordnung zuerst, Titel nur als Rueckfallebene.
    // Positionsnummern spielen bewusst keine Rolle mehr (siehe selectBillableServices).
    const billingServices = selectBillableServices(report.metadata.globalData?.services || [], 'vorank');

    // Punkt 1/2: gefüllte Felder, die von den Projektdaten abweichen. Reiner
    // Vergleich beim Rendern — kein Effekt, kein setData, also auch kein
    // Autospeichern. Erst der Knopf „Aus Projektdaten übernehmen" schreibt,
    // und zwar über denselben Weg wie jede Eingabe (setData → Autospeicher).
    const abweichungen = gleichePunktEinsZweiAb(data, report.metadata).abweichungen;

    // Inhaltskennung des Editorstands — nur, wenn es Fassungen gibt, gegen
    // die verglichen wird (Statuskarte). Der Plot rechnet sie beim Klick selbst.
    const aktuellerHash = fassungen ? vorankHash(data) : '';
    const plotFrageText = plotQuellenFrage ? vorankPlotFrage(plotQuellenFrage.fassung) : null;

    const uebernimmAusProjekt = (feld: AbgleichFeld, wert: string) => {
        setData(prev => ({ ...prev, [feld]: wert }));
    };

    // Aus der Planungsphase: reiner Vergleich beim Rendern, wie bei Punkt 1/2.
    // Erst der Knopf „Übernehmen" schreibt — über setData(prev => …), damit
    // `manualFields` vom jeweils aktuellen Stand ausgeht (Punkt 7 und 9 werden
    // dabei als von Hand gesetzt markiert, nichts anderes wird entsperrt).
    const planungsVorschlaege = steckbriefStand.steckbrief
        ? vorankuendigungVorschlaege(steckbriefStand.steckbrief, data)
        : [];

    const uebernimmAusPlanung = (v: VorankVorschlag) => {
        if (isReadOnly || !v.uebernahme) return;
        const was = v.uebernahme;
        setData(prev => wendeUebernahmeAn(prev, was));
        if (!v.codes.length) return;
        void stempleUebernommeneFragen(steckbriefProjektId, v.codes).then(meldung => {
            setStempelMeldung(meldung);
            // Gestempelt → der Steckbrief soll `uebernommen_am` kennen, sonst
            // nennt die nächste Zeile dieselben Codes noch einmal.
            if (meldung?.art === 'ok') steckbriefStand.neuLaden();
        });
    };

    // Dieselbe Regel wie beim Export: ohne gespeichertes Projekt und im
    // Lesemodus bleibt der Entwurfs-Knopf aus — mit dem Grund als Tooltip.
    const entwurfAbsage = ablageAbsage({ projectId: report.metadata.globalData?.projectId, isReadOnly });
    const ablageLaeuft = ablageStand?.art === 'laeuft';
    const plotSperre = vorankPlotSperre({ projectId: report.metadata.globalData?.projectId, baut: plotBaut });

    /**
     * Angekommene Plot-Aufträge dieses Projekts abrechnen wie einen Export —
     * aber erst, wenn kein anderer Abrechnungs- oder Abschlussschritt offen
     * ist (Muster SiGePlanEditor). Die Meldung kommt, wann der Server
     * antwortet, auch mitten in einer Export-Abrechnung; sie setzte sonst
     * deren Ziel auf „plot“, und die Bestätigung buchte den Plot statt zu
     * exportieren (Codex 22.10.2026). Jeder Plot bekommt seine eigene
     * Abrechnung, nacheinander. Auf dem Behörden-Reiter fehlen die Dialoge —
     * dort wartet er wie bei verlassener Ansicht bis zum nächsten Öffnen.
     */
    const abrechnungOffen = vorankAbrechnungFensterOffen({
        positionsauswahl: showBillingModal,
        fassungFrage: plotFassungFrage !== null,
        abschlussdialog: exportAbschluss !== null,
        stundenPflicht: stundenFensterOffen(abschluss.stundenPflicht.abfrage),
        startfrage: showInitDialog,
        ablageLaeuft,
        abschlussLaeuft: !!abschluss.stand?.laeuft,
        exportLaeuft: exportBereitet,
    });
    const plotAbholProjekt = plotAbrechnungAbholbar({
        offen: offenePlots,
        projektId: report.metadata.globalData?.projectId,
        isReadOnly,
        fensterOffen: abrechnungOffen || currentTab === 'authorities',
    });
    // Der Effekt ruft immer die Funktion des JÜNGSTEN Renders (Leistungen,
    // Pakete, Inhalt) — als Abhängigkeit liefe er sonst bei jedem Render.
    const starteAbrechnungRef = useRef(starteAbrechnung);
    useEffect(() => { starteAbrechnungRef.current = starteAbrechnung; });
    useEffect(() => {
        // Eine abgeholte, noch nicht abgerechnete gilt — nie überschreiben.
        if (!plotAbholProjekt || plotInAbrechnungRef.current) return;
        const naechster = nimmPlotAbrechnung(plotAbholProjekt, 'vorank');
        if (!naechster) return;
        plotInAbrechnungRef.current = naechster;
        starteAbrechnungRef.current({ art: 'plot', stand: naechster.stand });
    }, [plotAbholProjekt, offenePlots]);

    // --- MAIN RENDER ---
    
    // If Authorities Tab is selected, render AuthorityManager
    if (currentTab === 'authorities') {
        return <AuthorityManager isReadOnly={isReadOnly} />;
    }

    return (
        <div data-glass-view="module-vorank" data-glass-bereich="module" className="h-full overflow-y-auto bg-slate-50 flex flex-col animate-fade-in relative">
            
            {/* Startup Dialog */}
            {showInitDialog && (
                <div className="fixed inset-0 z-[150] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-scale-in">
                        <div className="bg-slate-900 text-white p-5 flex justify-between items-center">
                            <div className="flex items-center gap-3">
                                <FileText className="w-6 h-6 text-blue-400"/> 
                                <h3 className="text-lg font-bold">Vorankündigung öffnen</h3>
                            </div>
                        </div>
                        <div className="p-6 space-y-6">
                            <div className="bg-blue-50 border border-blue-100 p-4 rounded-xl">
                                <p className="text-sm font-medium text-blue-900">
                                    Es wurde bereits eine Vorankündigung für dieses Projekt erstellt.
                                </p>
                            </div>
                            <div className="space-y-4">
                                <button onClick={() => handleDialogChoice('edit')} className="w-full text-left p-4 rounded-xl border border-slate-200 hover:border-blue-400 hover:bg-slate-50 transition-all group flex items-center justify-between">
                                    <div><h4 className="font-bold text-slate-800 text-sm flex items-center gap-2 group-hover:text-blue-600"><Edit2 className="w-4 h-4"/> Bestehende bearbeiten</h4><p className="text-xs text-slate-500 mt-1">Inhalte behalten. Zählt als Revision.</p></div>
                                </button>
                                <div className="p-4 rounded-xl border-2 border-blue-100 bg-blue-50/30">
                                    <h4 className="font-bold text-slate-800 text-sm flex items-center gap-2 mb-3"><Copy className="w-4 h-4 text-blue-600"/> Version 2.0 erstellen</h4>
                                    <Button variant="primary" onClick={() => handleDialogChoice('new')} className="w-full bg-blue-600 hover:bg-blue-700 shadow-md">Neu erstellen</Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            
            {showVersionDialog && (
                <div className="fixed inset-0 z-[150] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-scale-in">
                        <div className="bg-slate-900 text-white p-5 flex items-center gap-3">
                            <Save className="w-6 h-6 text-blue-400"/>
                            <h3 className="text-lg font-bold">Version speichern</h3>
                        </div>
                        <div className="p-6 space-y-4">
                            <p className="text-sm text-slate-600">
                                Der aktuelle Stand der Vorankündigung wird als eigene Version im Archiv abgelegt. Bestehende Versionen bleiben unverändert.
                            </p>
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Bezeichnung</label>
                                <input
                                    autoFocus
                                    type="text"
                                    value={versionLabel}
                                    onChange={(e) => setVersionLabel(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') handleSaveVersion(); }}
                                    placeholder="z. B. Fassung für Bauherrn"
                                    className="w-full p-2.5 border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
                                />
                                <p className="text-xs text-slate-400 mt-1">Ohne Eingabe wird Datum und Uhrzeit verwendet.</p>
                            </div>
                            <div className="flex justify-end gap-3 pt-2">
                                <Button variant="secondary" onClick={() => setShowVersionDialog(false)}>Abbrechen</Button>
                                <Button variant="primary" onClick={handleSaveVersion} className="bg-blue-600 hover:bg-blue-700">Speichern</Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {exportAbschluss && (
                <div className="fixed inset-0 z-[150] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-scale-in">
                        <div className="bg-slate-900 text-white p-5 flex items-center gap-3">
                            {exportAbschluss.art === 'plot'
                                ? <Printer className="w-6 h-6 text-blue-400"/>
                                : <FileDown className="w-6 h-6 text-blue-400"/>}
                            <h3 className="text-lg font-bold">{vorankAbschlussTitel(exportAbschluss.art)}</h3>
                        </div>
                        <div className="p-6 space-y-4">
                            <p className="text-sm text-slate-600">
                                {vorankAbschlussSatz(exportAbschluss.art, exportAbschluss.gebucht)}
                            </p>
                            {exportAbschluss.hinweis && (
                                <div className="text-xs border rounded-lg px-3 py-2 flex items-start gap-2 bg-amber-50 border-amber-200 text-amber-800">
                                    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                                    <span className="flex-1 min-w-0 break-words">{exportAbschluss.hinweis}</span>
                                </div>
                            )}
                            {/* Begleitblatt: mit Link, ohne Link (fail-open) oder nicht nötig. */}
                            {exportAbschluss.begleitblatt && (
                                <div className={`text-xs border rounded-lg px-3 py-2 flex items-start gap-2 ${
                                    exportAbschluss.begleitblatt.ton === 'warnung'
                                        ? 'bg-amber-50 border-amber-200 text-amber-800'
                                        : 'bg-blue-50 border-blue-200 text-blue-900'
                                }`}>
                                    {exportAbschluss.begleitblatt.ton === 'warnung'
                                        ? <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                                        : <Info className="w-4 h-4 shrink-0 mt-0.5" />}
                                    <span className="flex-1 min-w-0 break-words">{exportAbschluss.begleitblatt.text}</span>
                                </div>
                            )}
                            {ablageStand && (
                                <AblageHinweis stand={ablageStand} isReadOnly={isReadOnly} onErneut={handleErneutAblegen} />
                            )}
                            {/* Plan B5, Fall 2/3: was der Versand an Bestandteilen erledigt hat. */}
                            <BestandteilAbschlussHinweis stand={abschluss.stand} />
                            <div className="flex justify-end gap-3 pt-2">
                                <Button variant="secondary" onClick={() => setExportAbschluss(null)}>Weiter bearbeiten</Button>
                                <Button
                                    variant="primary"
                                    onClick={() => { setExportAbschluss(null); onBack(); }}
                                    disabled={ablageLaeuft || !!abschluss.stand?.laeuft}
                                    title={ablageLaeuft || abschluss.stand?.laeuft ? 'Die Ablage läuft noch — einen Moment.' : undefined}
                                    className="bg-blue-600 hover:bg-blue-700"
                                >
                                    Zurück zum Projekt
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Stunden-Pflichtfenster (Stundenkontingent S3) — genau EINMAL je Modul,
                nicht im Hinweis: der steht oben im Exportdialog UND darunter. */}
            <BestandteilStundenPflicht pflicht={abschluss.stundenPflicht} />

            {showBillingModal && (
                <BillingSelectionModal
                    isOpen={showBillingModal}
                    onClose={schliesseAbrechnung}
                    onConfirm={executeExport}
                    services={billingServices}
                    title={vorankAbrechnungsTitel(abrechnungsZiel.art)}
                    context="vorank"
                    memoryScope={report.metadata.globalData?.projectId}
                    canSeeFinancials={rechte.hat('preise.sehen')}
                    vorabHinweis={schonGebuchtHinweis(billingServices)}
                />
            )}

            {/* Plan B5, Fall 2 beim Plot: keine eigene Vorankündigungs-Position,
                aber ein Paket mit dem Bestandteil. Statt still abzulegen die Frage,
                ob der Plot als Fassung hinausgeht — nur dann erledigt er den
                Bestandteil (Optik wie im SiGe-Plan-Editor). Der Export fragt hier
                nicht: sein Klick ist die Ausgabe. Über dem Plotter-Dialog (z-160):
                die Frage kommt, während er noch offen ist. */}
            {plotFassungFrage && (
                <div className="fixed inset-0 z-[170] bg-black/60 animate-fade-in backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
                        <div className="p-5 space-y-3">
                            <h3 className="text-base font-bold text-slate-800">{VORANK_FASSUNG_FRAGE_TITEL}</h3>
                            <p className="text-sm text-slate-600">
                                {vorankFassungFrageSatz(abschluss.pakete.map(p => p.title))}
                            </p>
                            <p className="text-xs text-slate-500">{VORANK_NICHT_ABLEGEN_HINWEIS}</p>
                        </div>
                        <div className="px-5 pb-5 flex flex-wrap justify-end gap-3">
                            <button
                                onClick={() => beantwortePlotFassungFrage(false)}
                                className="px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                            >
                                Nicht ablegen
                            </button>
                            <button
                                onClick={() => beantwortePlotFassungFrage(true)}
                                className="px-4 py-2 text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow transition-colors"
                            >
                                Als Fassung ausgeben
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Plan §9: unterschrieben ist nur ein älterer Stand — welcher wird
                geplottet? Ohne Unterschrift gibt es diese Frage nicht (E1). */}
            {plotQuellenFrage && plotFrageText && (
                <div className="fixed inset-0 z-[160] bg-black/60 animate-fade-in backdrop-blur-sm flex items-center justify-center p-4">
                    <div role="alertdialog" aria-modal="true" aria-label={plotFrageText.titel}
                        className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
                        <div className="p-5 space-y-3">
                            <h3 className="text-base font-bold text-slate-800">{plotFrageText.titel}</h3>
                            <p className="text-sm text-slate-600">{plotFrageText.text}</p>
                        </div>
                        <div className="px-5 pb-5 flex flex-col gap-2">
                            <button
                                type="button"
                                onClick={() => beantwortePlotQuellenFrage(true)}
                                className="px-4 py-2 text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow transition-colors"
                            >
                                {plotFrageText.unterschrieben}
                            </button>
                            <button
                                type="button"
                                onClick={() => beantwortePlotQuellenFrage(false)}
                                className="px-4 py-2 text-sm font-semibold text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 rounded-lg transition-colors"
                            >
                                {plotFrageText.stand}
                            </button>
                            <button
                                type="button"
                                autoFocus
                                onClick={() => setPlotQuellenFrage(null)}
                                className="px-4 py-2 text-sm font-semibold text-slate-500 hover:bg-slate-100 rounded-lg transition-colors"
                            >
                                Abbrechen
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {plotter && (
                <PlotterDialog
                    blob={plotter.blob}
                    blatt={plotter.blatt}
                    dokument={plotter.dokument}
                    onClose={schliessePlotter}
                    // Gemerkt mit dem beim Klick eingefrorenen Stand; die
                    // Abrechnung öffnet erst, wenn der Auftrag angekommen ist.
                    onNeuerAuftrag={id => merkePlotAuftrag(id, plotter.projektId, 'vorank', plotter.stand)}
                    onGesendet={meldePlotAngekommen}
                />
            )}

            <div data-glass="kopf" className="bg-white border-b border-slate-200 px-6 py-4 flex justify-between items-center sticky top-0 z-20 print:hidden">
                <div>
                     {/* Context handled by Sidebar */}
                     <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Modul-Tools</span>
                </div>
                <div className="flex flex-wrap gap-3 items-center justify-end">
                    <Button
                        variant="secondary"
                        icon={<FolderOpen className="w-4 h-4"/>}
                        onClick={() => setDokumenteOffen(true)}
                        title="Projektablage — Ansicht für die Vorankündigung"
                    >
                        Dokumente
                    </Button>
                    <Button
                        variant="secondary"
                        icon={<FolderInput className="w-4 h-4"/>}
                        onClick={handleEntwurfAblegen}
                        disabled={Boolean(entwurfAbsage) || ablageLaeuft}
                        title={
                            isReadOnly ? readOnlyTitle('Die Vorankündigung lässt sich nicht ablegen.')
                                : entwurfAbsage
                                || 'Den aktuellen Stand als Entwurf in die Projektablage legen — ohne Export, ohne Buchung'
                        }
                    >
                        Entwurf in Dokumente ablegen
                    </Button>
                    <Button variant="secondary" icon={<Save className="w-4 h-4"/>} onClick={() => { setVersionLabel(''); setShowVersionDialog(true); }}>
                        Version speichern
                    </Button>
                    {/* Aushang über den Plotter, wie beim SiGe-Plan. Nicht in der
                        Nur-Ansicht: gebucht und abgelegt würde dort nichts. */}
                    {!isReadOnly && (
                        <Button
                            variant="secondary"
                            icon={<Printer className="w-4 h-4"/>}
                            loading={plotBaut}
                            onClick={() => { void handlePlotClick(); }}
                            disabled={Boolean(plotSperre)}
                            title={plotSperre || VORANK_PLOT_KNOPF_TITEL}
                        >
                            An Plotter senden
                        </Button>
                    )}
                    {/* Plan §5: Begleitblatt für den Bauherrn mit QR/Link zur
                        digitalen Unterschrift. Nicht in der Nur-Ansicht — dort
                        entsteht keine Fassung, der Export bleibt wie bisher. */}
                    {!isReadOnly && (
                        <label
                            className="flex items-center gap-1.5 text-xs text-slate-600 select-none cursor-pointer"
                            title="Hängt an den Export ein Blatt für den Bauherrn an: Erläuterungen je Punkt, QR-Code und Link zur digitalen Unterschrift. Die Ablage in Dokumente bleibt ohne Begleitblatt. Auch ohne Haken legt der Export eine Fassung an (für den Papier-Rücklauf); ein Link zu einem älteren Stand gilt dann nicht mehr."
                        >
                            <input
                                type="checkbox"
                                checked={begleitblattAn}
                                onChange={e => setBegleitblattAn(e.target.checked)}
                                disabled={exportBereitet}
                                className="rounded border-slate-300"
                            />
                            Begleitblatt mit Unterschrift-Link anhängen
                        </label>
                    )}
                    <Button
                        variant="primary"
                        icon={<FileDown className="w-4 h-4"/>}
                        onClick={handleExportClick}
                        loading={exportBereitet}
                        title={exportBereitet ? 'Das PDF wird vorbereitet …' : undefined}
                        className="bg-blue-600 hover:bg-blue-700"
                    >
                        PDF Export & Speichern
                    </Button>
                </div>
            </div>

            <main className="flex-1 w-full max-w-7xl mx-auto p-4 md:p-8">
                {/* Ergebnis der letzten Ablage — auf jedem Reiter, solange der
                    Abschlussdialog es nicht schon zeigt. */}
                {ablageStand && !exportAbschluss && (
                    <div className="mb-4">
                        <AblageHinweis
                            stand={ablageStand}
                            isReadOnly={isReadOnly}
                            onErneut={handleErneutAblegen}
                            onSchliessen={() => setAblageStand(null)}
                        />
                    </div>
                )}
                {abschluss.stand && !exportAbschluss && (
                    <BestandteilAbschlussHinweis stand={abschluss.stand} onSchliessen={abschluss.zuruecksetzen} className="mb-4" />
                )}
                {/* Zum Plot: Blatt nicht entstanden, Abrechnung gescheitert oder
                    bewusst nicht abgerechnet — nie still. */}
                {plotMeldung && (
                    <div className={`mb-4 text-xs border rounded-lg px-3 py-2 flex items-start gap-2 print:hidden ${
                        plotMeldung.ton === 'fehler' ? 'bg-rose-50 border-rose-200 text-rose-800' : 'bg-amber-50 border-amber-200 text-amber-800'
                    }`}>
                        {plotMeldung.ton === 'fehler'
                            ? <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                            : <Info className="w-4 h-4 shrink-0 mt-0.5" />}
                        <span className="flex-1 min-w-0 break-words">{plotMeldung.text}</span>
                        <button
                            type="button"
                            onClick={() => setPlotMeldung(null)}
                            title="Hinweis ausblenden"
                            className="shrink-0 p-0.5 rounded hover:bg-black/5 transition"
                        >
                            <X className="w-3.5 h-3.5" />
                        </button>
                    </div>
                )}
                {currentTab === 'editor' && (
                    <>
                        {/* Unterschrift des Bauherrn (Plan §8/§9): Status der Fassungen,
                            Link/QR, Korrekturwunsch, unterschriebene PDF, Einlesen.
                            Ohne Fassungen-Tabelle (null) zeigt sie nichts. */}
                        <VorankUnterschriftKarte
                            projektId={fassungsProjektId}
                            fassungen={fassungen}
                            aktuellerHash={aktuellerHash}
                            isReadOnly={isReadOnly}
                            onNeuLaden={ladeFassungenNeu}
                        />
                        {/* Hinweis nur im Editor — nie auf Vorschau oder Behördenblatt. */}
                        {abweichungen.length > 0 && (
                            <div className="mb-4 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 print:hidden">
                                <div className="flex items-start gap-2 font-bold">
                                    <Info className="w-4 h-4 shrink-0 mt-0.5" />
                                    <span>Punkt 1/2 weichen von den Projektdaten ab. Die Eingaben bleiben stehen; bei Bedarf je Feld übernehmen.</span>
                                </div>
                                <ul className="mt-2 space-y-2">
                                    {abweichungen.map(a => (
                                        <li key={a.feld} className="flex flex-col md:flex-row md:items-center gap-1 md:gap-3 pl-6">
                                            <div className="min-w-0 flex-1">
                                                <span className="font-semibold">{ABGLEICH_FELD_LABEL[a.feld]}:</span>{' '}
                                                <span className="whitespace-pre-line break-words">Projektdaten „{a.projekt}"</span>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => uebernimmAusProjekt(a.feld, a.projekt)}
                                                disabled={isReadOnly}
                                                className="shrink-0 flex items-center gap-1 text-[11px] text-blue-700 bg-white border border-blue-200 hover:bg-blue-50 px-2 py-1 rounded disabled:opacity-50 disabled:cursor-not-allowed transition"
                                                title={isReadOnly ? 'Projekt ist schreibgeschützt' : 'Den Wert aus den Projektdaten in dieses Feld schreiben'}
                                            >
                                                <RotateCcw className="w-3 h-3" /> Aus Projektdaten übernehmen
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {/* Änderungsdienst (Stufe 6.1): weicht die versendete Fassung vom
                            heutigen Stand der Planungsphase ab, steht die Rückfrage hier —
                            über der Leiste, denn sie betrifft das Blatt, das schon
                            hinausging. Nur lesend; geschrieben wird dort allein der
                            Entscheid, nie etwas an der Vorankündigung. */}
                        {steckbriefProjektId && steckbriefProjektId !== 'temp-id' && (
                            <AbweichungsHinweis
                                projectId={steckbriefProjektId}
                                metadata={report.metadata}
                                unterlage="vorankuendigung"
                                isReadOnly={isReadOnly}
                                onNeueFassung={() => setNeueFassungHinweis(true)}
                            />
                        )}
                        {neueFassungHinweis && (
                            <div className="mb-4 text-xs text-blue-900 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 flex items-start gap-2 print:hidden">
                                <Info className="w-4 h-4 shrink-0 mt-0.5 text-blue-600" />
                                <span className="flex-1 min-w-0 break-words">
                                    Neue Fassung: Die Vorankündigung kennt keinen eigenen Entwurf — die Angaben hier bearbeiten
                                    (die Leiste „Aus der Planungsphase" zeigt die geänderten Werte) und erneut exportieren.
                                    Der Export ist die neue Fassung; die bisherige bleibt im Archiv und in Dokumenten.
                                </span>
                                <button
                                    type="button"
                                    onClick={() => setNeueFassungHinweis(false)}
                                    title="Hinweis ausblenden"
                                    className="shrink-0 p-0.5 rounded hover:bg-black/5 transition"
                                >
                                    <X className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        )}
                        {/* Erst wenn der Steckbrief zu DIESEM Projekt gelesen ist —
                            `null` davor heißt „noch nicht gelesen", nicht „nichts da". */}
                        {steckbriefStand.bereit && (
                            <VorankuendigungPlanungsLeiste
                                vorschlaege={planungsVorschlaege}
                                ausgeblendet={planungAusgeblendet}
                                isReadOnly={isReadOnly}
                                lesefehler={steckbriefStand.fehler}
                                stempel={stempelMeldung}
                                onUebernehmen={uebernimmAusPlanung}
                                onAusblenden={id => setPlanungAusgeblendet(prev => new Set([...prev, id]))}
                                onStempelSchliessen={() => setStempelMeldung(null)}
                            />
                        )}
                        <FormEditor
                            data={data}
                            onChange={setData}
                            metadata={report.metadata}
                            settings={report.settings}
                        />
                    </>
                )}
                
                {currentTab === 'preview' && (
                    <div data-glass="buehne" className="flex justify-center bg-gray-100/50 p-4 rounded-xl">
                        <FormPreview data={data} id="print-area" />
                    </div>
                )}
                
                {currentTab === 'archive' && (
                    <div className="space-y-6">
                        <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2"><Archive className="w-5 h-5 text-blue-600"/> Versionen</h2>
                                <Button variant="secondary" icon={<Save className="w-4 h-4"/>} onClick={() => { setVersionLabel(''); setShowVersionDialog(true); }} className="text-xs">Version speichern</Button>
                            </div>
                            {data.archive && data.archive.length > 0 ? (
                                <div className="space-y-3">
                                    {data.archive.map((entry, idx) => (
                                        <div key={entry.id || idx} className="flex items-center justify-between gap-4 p-4 bg-slate-50 rounded-xl border border-slate-200 hover:border-blue-300 transition-colors">
                                            <div className="flex items-center gap-4 min-w-0">
                                                <div className="bg-white p-2 rounded-lg border border-slate-200 text-blue-600 shrink-0"><FileClock className="w-5 h-5"/></div>
                                                <div className="min-w-0">
                                                    <div className="text-sm font-bold text-slate-800 truncate">{describeVorankuendigungVersion(entry)}</div>
                                                    <div className="text-xs text-slate-500">Vorankündigung vom {new Date(entry.date).toLocaleDateString('de-DE')}</div>
                                                </div>
                                            </div>
                                            <div className="flex gap-2 shrink-0">
                                                <Button variant="secondary" onClick={() => handleLoadVersion(entry)} className="text-xs" icon={<Upload className="w-4 h-4"/>}>In Editor laden</Button>
                                                <Button variant="secondary" onClick={() => handleRegeneratePdf(entry)} className="text-xs" icon={<FileDown className="w-4 h-4"/>}>PDF Erstellen</Button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-12 border-2 border-dashed border-slate-200 rounded-xl bg-slate-50"><Archive className="w-10 h-10 text-slate-300 mx-auto mb-2"/><p className="text-slate-500 font-medium">Noch keine archivierten Versionen vorhanden.</p></div>
                            )}
                        </div>
                    </div>
                )}
            </main>

            {/* Bewusst OHNE Bedingung auf die Projekt-Id: fehlte sie, passierte
                nach dem Klick sonst kommentarlos nichts. Das Panel erkennt ein
                noch nicht gespeichertes Projekt selbst (auch 'temp-id') und
                sagt es. */}
            {dokumenteOffen && (
                <DokumentePanel
                    projectId={report.metadata.globalData?.projectId || ''}
                    modul="vorankuendigung"
                    userProfile={userProfile}
                    isReadOnly={isReadOnly}
                    onSchliessen={() => setDokumenteOffen(false)}
                />
            )}
        </div>
    );
};
